/**
 * AIT-539 — a marker for "this document is about to hard-navigate into a
 * different organization".
 *
 * Re-scoping the session is a two-step move: `switchOrganization` re-seals the
 * cookie, and only then does the caller `window.location.assign` the new org's
 * URL. In between, any view still holding the PREVIOUS :orgId refetches it
 * against the new cookie and gets a cross-org 403. That 403 must not be read as
 * a denial — but a view cannot tell it apart from a legitimately out-of-scope
 * org unless it knows a navigation is coming.
 *
 * Module state, deliberately NOT sessionStorage. The marker only has a reader
 * in the document that set it: the destination document arrives already scoped
 * correctly and never asks. Persisting it bought nothing and cost a permanent
 * stuck state — a marker left behind by an interrupted navigation survived
 * every subsequent reload of that tab and made the next out-of-scope org look
 * like a navigation that was still coming. Living in module state, it dies with
 * the document that created it.
 *
 * The lease bounds the remaining case: a navigation cancelled (Stop, a
 * `beforeunload`, a restored page) inside a still-living document. It is
 * generous because it is not discriminating anything — it only decides when to
 * give up on a navigation that is clearly never arriving. Expiring simply
 * re-scopes into the org the URL is already showing, which is the right
 * recovery.
 */
export const ORG_NAVIGATION_LEASE_MS = 30_000;

let pending: { target: string; at: number } | null = null;

export function markOrgNavigationPending(targetOrgPublicId: string): void {
  pending = { target: targetOrgPublicId, at: Date.now() };
}

export function clearOrgNavigationPending(): void {
  pending = null;
}

/** The org this document is navigating to, or null once arrived or expired. */
export function getPendingOrgNavigation(): string | null {
  if (!pending) return null;
  if (Date.now() - pending.at > ORG_NAVIGATION_LEASE_MS) {
    pending = null;
    return null;
  }
  return pending.target;
}

/**
 * Milliseconds until the current marker expires, or 0 when there is none.
 *
 * A cancelled navigation changes nothing a React effect depends on, so a purely
 * pull-based lease would never be observed: the waiting view would sit there
 * long after the marker went stale. Callers use this to wake up exactly once at
 * the boundary and re-evaluate.
 */
export function msUntilPendingOrgNavigationExpires(): number {
  if (!pending) return 0;
  return Math.max(0, ORG_NAVIGATION_LEASE_MS - (Date.now() - pending.at));
}
